import React, { useState, useEffect } from 'react';
import { Box, Grid, useTheme, useMediaQuery, Snackbar, Alert } from '@mui/material';
import { Loading } from '../UI/Loading';
import { useProfile } from '../../hooks/useProfile';
import { ProfileUpdateData, PasswordChangeData } from '../../types/profile';
import {
  UserInfoCard,
  ProfileForm,
  PasswordChangeForm,
  ShowPasswordsState,
  CareerProfileForm,
} from './Profile';
import { SalesExperienceForm } from './Profile/SalesExperienceForm';
import { ScoutEmailSettings } from '../ScoutSettings';

export const ProfileSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // lgより小さい場合は縦積み

  const {
    user,
    isLoading,
    isUpdating,
    error,
    successMessage,
    updateProfile,
    changePassword,
    clearError,
    clearSuccessMessage,
    refreshProfile,
  } = useProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileUpdateData>({
    fullName: '',
    furigana: '',
    gender: undefined,
    bio: '',
    phone: '',
    organization: '',
    mediationCount: undefined,
    monthlyMaxSales: undefined,
    totalSales: undefined,
    realEstateExperienceMonths: undefined,
    entrepreneurialInterest: undefined,
    // キャリア・希望情報
    currentPrefecture: undefined,
    desiredPrefecture: undefined,
    qualifications: undefined,
    desiredJobType: undefined,
    // 学歴情報
    birthDate: undefined,
    university: undefined,
    faculty: undefined,
    graduationYear: undefined,
    graduationStatus: undefined,
  });

  // パスワード変更用のstate
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 営業実績・経験情報編集用のstate
  const [isEditingSalesExperience, setIsEditingSalesExperience] =
    useState(false);

  // キャリア・希望情報編集用のstate
  const [isEditingCareerProfile, setIsEditingCareerProfile] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState<ShowPasswordsState>({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (user) {
      setFormData(prevFormData => {
        // 基本プロフィール編集中の場合は、formDataを上書きしない
        if (isEditing) {
          return prevFormData;
        }
        
        const newFormData = {
          fullName: user.fullName || '',
          furigana: user.furigana || '',
          gender: user.gender,
          bio: user.bio || '',
          phone: user.phone || '',
          organization: user.organization || '',
          mediationCount: user.mediationCount,
          monthlyMaxSales: user.monthlyMaxSales,
          totalSales: user.totalSales,
          realEstateExperienceMonths: user.realEstateExperienceMonths,
          entrepreneurialInterest: user.entrepreneurialInterest,
          // キャリア・希望情報
          currentPrefecture: user.currentPrefecture,
          desiredPrefecture: user.desiredPrefecture,
          qualifications: user.qualifications,
          desiredJobType: user.desiredJobType,
          // 学歴情報
          birthDate: user.birthDate,
          university: user.university,
          faculty: user.faculty,
          graduationYear: user.graduationYear,
          graduationStatus: user.graduationStatus,
        };
        
        return newFormData;
      });
    }
  }, [user, isEditing, isEditingSalesExperience, isEditingCareerProfile]);

  // 資格専用のハンドラー
  const handleQualificationsChange = (qualifications: string[]) => {
    setFormData((prev) => ({
      ...prev,
      qualifications,
    }));
  };

  const handleInputChange =
    (field: keyof ProfileUpdateData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // 数値フィールド（売上実績含む）は数値として処理
      if (
        [
          'monthlyMaxSales',
          'totalSales',
          'mediationCount',
          'realEstateExperienceMonths',
          'graduationYear',
        ].includes(field)
      ) {
        const cleanValue = value.replace(/,/g, ''); // コンマを除去
        const numericValue =
          cleanValue === '' || cleanValue == null
            ? undefined
            : parseInt(cleanValue, 10);
        setFormData((prev) => ({
          ...prev,
          [field]:
            numericValue === undefined || isNaN(numericValue)
              ? undefined
              : numericValue,
        }));
      } else if (field === 'entrepreneurialInterest') {
        // ブール値フィールドの場合
        const booleanValue =
          value === '' || value == null ? undefined : value === 'true';
        setFormData((prev) => ({
          ...prev,
          [field]: booleanValue,
        }));
      } else if (field === 'qualifications') {
        // 資格フィールドの場合（配列）
        // このケースは直接handleQualificationsChangeを使用することを推奨
        setFormData((prev) => ({
          ...prev,
          [field]: value as unknown as string[],
        }));
      } else {
        // 文字列フィールドの場合
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
      }
    };

  const handleSave = async () => {
    try {
      // 基本プロフィール情報のみを送信
      const basicProfileData = {
        fullName: formData.fullName,
        furigana: formData.furigana,
        gender: formData.gender,
        bio: formData.bio,
        phone: formData.phone,
        organization: formData.organization,
      };
      await updateProfile(basicProfileData);
      setIsEditing(false);
      clearError();
    } catch (error) {
      // Error is handled by the context
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        furigana: user.furigana || '',
        gender: user.gender,
        bio: user.bio || '',
        phone: user.phone || '',
        organization: user.organization || '',
        mediationCount: user.mediationCount,
        monthlyMaxSales: user.monthlyMaxSales,
        totalSales: user.totalSales,
        realEstateExperienceMonths: user.realEstateExperienceMonths,
        entrepreneurialInterest: user.entrepreneurialInterest,
      });
    }
    setIsEditing(false);
    clearError();
  };

  // 営業実績・経験情報のハンドラー
  const handleSalesExperienceSave = async () => {
    try {
      // フォームデータはすでに数値型として管理されているため、直接使用
      const salesExperienceData = {
        mediationCount: formData.mediationCount,
        monthlyMaxSales: formData.monthlyMaxSales,
        totalSales: formData.totalSales,
        realEstateExperienceMonths: formData.realEstateExperienceMonths,
        entrepreneurialInterest: formData.entrepreneurialInterest,
      };

      await updateProfile(salesExperienceData);

      setIsEditingSalesExperience(false);
      clearError();
    } catch (error) {
      // Error is handled by the context
    }
  };

  const handleSalesExperienceCancel = () => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        mediationCount: user.mediationCount,
        monthlyMaxSales: user.monthlyMaxSales,
        totalSales: user.totalSales,
        realEstateExperienceMonths: user.realEstateExperienceMonths,
        entrepreneurialInterest: user.entrepreneurialInterest,
      }));
    }
    setIsEditingSalesExperience(false);
    clearError();
  };

  // キャリア・希望情報のハンドラー
  const handleCareerProfileSave = async () => {
    try {
      const careerProfileData = {
        currentPrefecture: formData.currentPrefecture,
        desiredPrefecture: formData.desiredPrefecture,
        qualifications: formData.qualifications,
        desiredJobType: formData.desiredJobType,
        // 学歴情報
        birthDate: formData.birthDate,
        university: formData.university,
        faculty: formData.faculty,
        graduationYear: formData.graduationYear,
        graduationStatus: formData.graduationStatus,
      };

      await updateProfile(careerProfileData);

      setIsEditingCareerProfile(false);
      clearError();
    } catch (error) {
      // Error is handled by the context
    }
  };

  const handleCareerProfileCancel = () => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        currentPrefecture: user.currentPrefecture,
        desiredPrefecture: user.desiredPrefecture,
        qualifications: user.qualifications,
        desiredJobType: user.desiredJobType,
        // 学歴情報
        birthDate: user.birthDate,
        university: user.university,
        faculty: user.faculty,
        graduationYear: user.graduationYear,
        graduationStatus: user.graduationStatus,
      }));
    }
    setIsEditingCareerProfile(false);
    clearError();
  };

  // パスワード変更用のハンドラー
  const handlePasswordChange =
    (field: keyof PasswordChangeData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handlePasswordSave = async () => {
    try {
      await changePassword(passwordData);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
      clearError();
    } catch (error) {
      // Error is handled by the context
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setIsChangingPassword(false);
    clearError();
  };

  const togglePasswordVisibility = (field: keyof ShowPasswordsState) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        エラー: ユーザー情報を取得できませんでした。
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      {/* User Info Card - レスポンシブ対応 */}
      <Grid item xs={12} lg={4} sx={{ order: { xs: 1, lg: 1 } }}>
        <UserInfoCard
          user={{
            ...user,
            bio: user.bio,
            phone: user.phone,
            organization: user.organization,
            mediationCount: user.mediationCount,
            monthlyMaxSales: user.monthlyMaxSales,
            totalSales: user.totalSales,
            realEstateExperienceMonths: user.realEstateExperienceMonths,
            entrepreneurialInterest: user.entrepreneurialInterest,
          }}
        />
      </Grid>

      {/* Profile Details - レスポンシブ対応 */}
      <Grid item xs={12} lg={8} sx={{ order: { xs: 2, lg: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 3 },
          }}
        >
          {/* Sales Experience Section - 最優先で表示 */}
          <SalesExperienceForm
            formData={{
              mediationCount: formData.mediationCount,
              monthlyMaxSales: formData.monthlyMaxSales,
              totalSales: formData.totalSales,
              realEstateExperienceMonths: formData.realEstateExperienceMonths,
              entrepreneurialInterest: formData.entrepreneurialInterest,
            }}
            isEditing={isEditingSalesExperience}
            isUpdating={isUpdating}
            error={isEditingSalesExperience ? error : null}
            onInputChange={handleInputChange}
            onSave={handleSalesExperienceSave}
            onCancel={handleSalesExperienceCancel}
            onEdit={() => setIsEditingSalesExperience(true)}
            onClearError={clearError}
          />

          {/* Career Profile Section */}
          <CareerProfileForm
            formData={formData}
            isEditing={isEditingCareerProfile}
            isUpdating={isUpdating}
            error={isEditingCareerProfile ? error : null}
            onInputChange={handleInputChange}
            onQualificationsChange={handleQualificationsChange}
            onSave={handleCareerProfileSave}
            onCancel={handleCareerProfileCancel}
            onEdit={() => setIsEditingCareerProfile(true)}
            onClearError={clearError}
          />

          {/* Basic Profile Form */}
          <ProfileForm
            formData={formData}
            isEditing={isEditing}
            isUpdating={isUpdating}
            error={isEditing ? error : null}
            onInputChange={handleInputChange}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onClearError={clearError}
          />

          {/* Password Change Section */}
          <PasswordChangeForm
            passwordData={passwordData}
            isChangingPassword={isChangingPassword}
            isUpdating={isUpdating}
            error={isChangingPassword ? error : null}
            showPasswords={showPasswords}
            onPasswordChange={handlePasswordChange}
            onPasswordSave={handlePasswordSave}
            onPasswordCancel={handlePasswordCancel}
            onTogglePasswordVisibility={togglePasswordVisibility}
            onStartChange={() => setIsChangingPassword(true)}
            onClearError={clearError}
          />

          {/* Scout Email Settings (学生のみ) */}
          {user?.userRole === 'student' && <ScoutEmailSettings />}
        </Box>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={clearSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clearSuccessMessage}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ProfileSection;
